import { SIGNALS_BY_COUNTRY } from './constants';
import { __DetectCountry } from './types';

const DEFAULT_THRESHOLD = 0.25;

const COUNTRY_WEIGHTS = {
  KEYWORK: 0.45,
  CITY: 0.25,
  REGION_LONG: 0.3,
  REGION_SHORT: 0.15,
  POSTAL: 0.5,
};

const scoreText = (
  text: string,
  signals: __DetectCountry.__CountrySignals
): number => {
  let score = 0;

  if (signals.postalCode && signals.postalCode.test(text)) {
    score += COUNTRY_WEIGHTS.POSTAL;
  }

  if (signals.keywords.some(rx => rx.test(text))) {
    score += COUNTRY_WEIGHTS.KEYWORK;
  }

  if (signals.cities.some(rx => rx.test(text))) {
    score += COUNTRY_WEIGHTS.CITY;
  }

  if (signals.regionsLong.some(rx => rx.test(text))) {
    score += COUNTRY_WEIGHTS.REGION_LONG;
  }
  if (signals.regionsShort.some(rx => rx.test(text))) {
    score += COUNTRY_WEIGHTS.REGION_SHORT;
  }

  return Math.min(1, score);
};

export const detectCountry = (
  input: string,
  options?: { threshold?: number }
): __DetectCountry.__CountryDetectionResult => {
  if (!input || typeof input !== 'string') {
    return { country: null, confidence: 0 };
  }

  // Normalize whitespace and case to make matching more robust
  const text = input.replace(/\s+/g, ' ').trim();
  const threshold = options?.threshold ?? DEFAULT_THRESHOLD;

  const scores: Array<{
    country: __DetectCountry.__SupportedCountry;
    confidence: number;
  }> = [
    { country: 'US', confidence: scoreText(text, SIGNALS_BY_COUNTRY.US) },
    { country: 'UK', confidence: scoreText(text, SIGNALS_BY_COUNTRY.UK) },
  ];

  scores.sort((a, b) => b.confidence - a.confidence);
  const best = scores[0];

  if (best.confidence >= threshold) {
    return { country: best.country, confidence: best.confidence };
  }

  return { country: null, confidence: best.confidence };
};

export const detectCountryFor = (
  input: string,
  country: __DetectCountry.__SupportedCountry,
  options?: { threshold?: number }
): { match: boolean; confidence: number } => {
  if (!input || typeof input !== 'string') {
    return { match: false, confidence: 0 };
  }

  // Normalize whitespace and case to make matching more robust
  const text = input.replace(/\s+/g, ' ').trim();
  const threshold = options?.threshold ?? DEFAULT_THRESHOLD;
  const signals = SIGNALS_BY_COUNTRY[country];
  const confidence = scoreText(text, signals);

  return { match: confidence >= threshold, confidence };
};
